/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import TwilioVideo, {
  ConnectOptions,
  CreateLocalTrackOptions,
  CreateLocalTracksOptions,
  LocalTrack as TwilioLocalTrack,
  LocalAudioTrack as TwilioLocalAudioTrack,
  LocalVideoTrack as TwilioLocalVideoTrack,
  PreflightTestReport,
  runPreflight,
} from 'twilio-video';
import { mobileUtils } from '@blend/consumer-components';
import { DEFAULT_MOBILE_VIDEO_CONSTRAINTS, DEFAULT_VIDEO_CONSTRAINTS } from '../../../definitions/devices';
import { participantAPI } from '../../../api/participant';
import { getDeviceInfo, logEvent } from '../utils';
import { getTwilioVideoOptions } from '../hooks/twilioVideoOptions';
import { VideoProviderName } from '../types';
import { getVideoVendorApiKey } from '@utils/videoVendorStorageUtils';
import { MemoizedTwilioParticipantStrip } from '@components/videoProvider/twilio/TwilioParticipantStrip/TwilioParticipantStrip';
import { ParticipantTracks } from '@components/videoProvider/twilio/ParticipantTracks/ParticipantTracks';
import {
  getPreflightQualityScore,
  PreflightQualityScore,
} from '@components/form/TaskInputs/NetworkConnection/Preflight/getPreflightQualityScore';

export class TwilioVideoProvider implements VideoProvider.IProvider {
  name = VideoProviderName.TWILIO;
  private twilioRoom?: VideoProvider.IRoom;

  async connect(
    token: string,
    callback: () => void,
    meta: {
      twilioLocalTracks?: (VideoProvider.ILocalAudioTrack | VideoProvider.ILocalVideoTrack)[];
      opts?: ConnectOptions;
    },
  ): Promise<{ disconnect: () => void; session: VideoProvider.IRoom }> {
    const { twilioLocalTracks, opts } = meta;
    this.twilioRoom = (await TwilioVideo.connect(token, {
      ...opts,
      ...getTwilioVideoOptions(),
      audio: true,
      tracks: twilioLocalTracks as TwilioLocalTrack[],
    })) as unknown as VideoProvider.IRoom;
    const disconnect = () => this.twilioRoom!.disconnect();

    this.twilioRoom.once('disconnected', async (room, error) => {
      // Reset the room after all other disconnected listeners have been called
      setTimeout(() => callback());

      if (error) {
        const eventData = {
          preflight: false,
          browserTime: new Date(),
          message: error.message,
          level: 'error',
          data: { error, room },
        };
        await participantAPI.rooms.sendBrowserLog(eventData);
      }
    });

    return {
      disconnect,
      // todo: do not return twilioRoom to avoid direct access to SDK methods. instead use abstract methods to access the Room object
      session: this.twilioRoom,
    };
  }

  leaveSession() {
    this.twilioRoom?.disconnect();
  }

  isCallConnected() {
    return Boolean(this.twilioRoom);
  }

  async createLocalTracks(
    selectedAudioDeviceId: string | null,
    selectedVideoDeviceId: string | null,
    isCameraPermissionDenied?: boolean,
    isMicrophonePermissionDenied?: boolean,
  ): Promise<{
    audioTrack?: VideoProvider.ILocalAudioTrack;
    videoTrack?: VideoProvider.ILocalVideoTrack;
    updatedVideoDeviceId?: string;
    updatedAudioDeviceId?: string;
  }> {
    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices } = await getDeviceInfo();
    if (!hasAudioInputDevices && !hasVideoInputDevices) {
      return Promise.resolve({});
    }

    const hasSelectedAudioDevice = audioInputDevices.some(
      device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId,
    );
    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId,
    );

    const shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;
    const shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;

    const constraints = mobileUtils.isMobileDevice() ? DEFAULT_MOBILE_VIDEO_CONSTRAINTS : DEFAULT_VIDEO_CONSTRAINTS;

    const localTrackConstraints: CreateLocalTracksOptions = {
      video: shouldAcquireVideo && {
        ...(constraints as {}),
        name: `camera-${Date.now()}`,
        ...(hasSelectedVideoDevice && { deviceId: { exact: selectedVideoDeviceId! } }),
      },
      audio: shouldAcquireAudio && {
        ...(hasSelectedAudioDevice && { deviceId: { exact: selectedAudioDeviceId! } }),
      },
    };

    const tracks = await TwilioVideo.createLocalTracks(localTrackConstraints);
    const audioTrack = tracks.find(track => track.kind === 'audio') as TwilioLocalAudioTrack;
    const videoTrack = tracks.find(track => track.kind === 'video') as TwilioLocalVideoTrack;
    const updatedVideoDeviceId = videoTrack?.mediaStreamTrack.getSettings().deviceId ?? '';

    return { audioTrack, videoTrack, updatedVideoDeviceId };
  }

  async createLocalVideoTrack(selectedVideoDeviceId: string | null) {
    const { videoInputDevices } = await getDeviceInfo();

    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId,
    );
    const constraints = mobileUtils.isMobileDevice() ? DEFAULT_MOBILE_VIDEO_CONSTRAINTS : DEFAULT_VIDEO_CONSTRAINTS;

    const options: CreateLocalTrackOptions = {
      ...(constraints as {}),
      name: `camera-${Date.now()}`,
      ...(hasSelectedVideoDevice && { deviceId: { exact: selectedVideoDeviceId! } }),
    };

    return (await TwilioVideo.createLocalVideoTrack(options)) as unknown as VideoProvider.ILocalVideoTrack;
  }

  async createLocalAudioTrack(selectedAudioDeviceId: string | null) {
    const { audioInputDevices } = await getDeviceInfo();
    const hasSelectedAudioDevice = audioInputDevices.some(
      device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId,
    );

    const options: CreateLocalTrackOptions = {
      ...(hasSelectedAudioDevice && { deviceId: { exact: selectedAudioDeviceId! } }),
    };

    return (await TwilioVideo.createLocalAudioTrack(options)) as unknown as VideoProvider.ILocalAudioTrack;
  }

  getMediaTrackOrStream(track?: VideoProvider.ITrack): MediaStream | MediaStreamTrack | undefined {
    return track?.mediaStreamTrack;
  }
  onTrackStarted(track: VideoProvider.ITrack, callback: () => void) {
    track.on!('started', callback);
  }

  onTrackStopped(track: VideoProvider.ITrack, callback: () => void) {
    track.off!('started', callback);
  }

  isCameraFrontFacing = (mediaStreamOrTrack: MediaStream | MediaStreamTrack) => {
    return mediaStreamOrTrack instanceof MediaStreamTrack
      ? mediaStreamOrTrack.getSettings().facingMode !== 'environment'
      : false;
  };

  streamLocalVideo(_cameraId?: string): undefined {
    // This method is specific to zoom. Streaming video is handled via Tracks for Twilio
    return;
  }

  detachVideoFromVideoPlayer(_userId: number): Promise<void> {
    // This method is specific to zoom. Streaming video is handled via Tracks for Twilio
    return new Promise(resolve => {
      resolve();
    });
  }

  streamLocalAudio(_microphoneId?: string): undefined {
    // This method is specific to zoom. Streaming audio is handled via Tracks for Twilio
    return;
  }
  stopLocalVideo(_cameraId?: string): undefined {
    // This method is specific to zoom. Streaming video is handled via Tracks for Twilio
    return;
  }

  stopLocalAudio(_microphoneId?: string): undefined {
    // This method is specific to zoom. Streaming audio is handled via Tracks for Twilio
    return;
  }

  attachVideoToVideoPlayer(_userId: number, _videoPlayer: VideoProvider.ZoomVideoPlayer) {
    throw new Error('Method not required for Twilio');
  }

  streamSwitchCamera(_deviceId: string): Promise<void> {
    // This method is specific to zoom. Streaming video is handled via Tracks for Twilio
    return new Promise(resolve => {
      resolve();
    });
  }

  streamSwitchMicrophone(
    deviceId: string,
    localAudioTrack: VideoProvider.ILocalAudioTrack,
    _getLocalAudioTrack: () => Promise<VideoProvider.ILocalAudioTrack>,
  ): Promise<void> {
    return new Promise(async resolve => {
      await localAudioTrack?.restart?.({ deviceId: { exact: deviceId } });
      resolve();
    });
  }

  isSupportMultipleVideos() {
    // Twilio doesn't have this problem that when running on firefox more than 1 stream video-players glitch and flicker
    return true;
  }

  /**
   * Preflight test related method
   */
  runPreflightTest(
    roomId: string,
    updatePreflightReport: (
      preflightReport: VideoProvider.PreflightResults<PreflightTestReport, PreflightQualityScore>,
    ) => void,
  ) {
    const videoAuthToken = getVideoVendorApiKey(roomId);
    if (!videoAuthToken) {
      logEvent('warn', `Could not run preflight test - twilio api token was not found for this session`, true);
      return;
    }

    const preflightTest = runPreflight(videoAuthToken);
    preflightTest.on('progress', progressEvent => {
      logEvent('info', `preflight test progress: ${progressEvent}`, true, { progressEvent });
    });

    preflightTest.on('failed', (error, report) => {
      logEvent('error', `preflight test error`, true, {
        error,
        report,
      });
      updatePreflightReport({
        preflightReport: report,
        qualityScore: getPreflightQualityScore(report),
      });
    });

    preflightTest.on('completed', report => {
      const qualityScore = getPreflightQualityScore(report);
      logEvent('info', `preflight test completed`, true, {
        report,
        qualityScore,
      });
      updatePreflightReport({
        preflightReport: report,
        qualityScore,
      });
    });
  }

  /**
   * Participants related methods
   */
  getParticipants(): VideoProvider.IParticipant[] {
    return Array.from(this.twilioRoom?.participants?.values() || []);
  }

  getCurrentUserId(): number {
    return 0;
  }

  /** Event Listeners **/
  onParticipantConnected(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  onParticipantDisconnected(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  onParticipantUpdated(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  offParticipantConnected(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  offParticipantDisconnected(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  offParticipantUpdated(_callback: (participants?: VideoProvider.IParticipant[]) => void) {}
  onDominantSpeakerChanged(callback: (participant: VideoProvider.IParticipant) => void) {
    this.twilioRoom?.on?.('dominantSpeakerChanged', callback);
  }
  offDominantSpeakerChanged(callback: (participant: VideoProvider.IParticipant) => void) {
    this.twilioRoom?.off?.('dominantSpeakerChanged', callback);
  }
  isDominantSpeaker(participant?: VideoProvider.IParticipant, dominantSpeaker?: VideoProvider.IParticipant | null) {
    return dominantSpeaker === participant;
  }

  onDevicePermissionsChanged() {}
  offDevicePermissionsChanged() {}
  onNetworkQualityLevelChanged(
    participant: VideoProvider.IParticipant,
    callback: (networkQualityLevel: number) => void,
  ) {
    participant.on!('networkQualityLevelChanged', callback);
  }
  offNetworkQualityLevelChanged(
    participant: VideoProvider.IParticipant,
    callback: (networkQualityLevel: number) => void,
  ) {
    participant.off!('networkQualityLevelChanged', callback);
  }

  /**
   * Closed Captioning related methods
   */
  isClosedCaptioningSupported() {
    return false;
  }
  async startClosedCaptioning(): Promise<void> {
    // Dummy await to satisfy linter
    await Promise.resolve();
    throw new Error('Feature not support for Twilio and should not be called');
  }
  onClosedCaptioningMessage(_callback: (message: VideoProvider.ZoomCaptionPayload) => void) {
    throw new Error('Feature not support for Twilio and should not be called');
  }
  offloadClosedCaptioningMessage(_callback: (message: VideoProvider.ZoomCaptionPayload) => void) {
    throw new Error('Feature not support for Twilio and should not be called');
  }
  disableClosedCaptioning(): Promise<void> {
    throw new Error('Feature not support for Twilio and should not be called');
  }

  /**
   * Components and CSS overrides
   */
  getParticipantStripComponent(): React.NamedExoticComponent<{ isParticipantsTabView?: boolean }> {
    return MemoizedTwilioParticipantStrip;
  }
  getParticipantTrackComponent() {
    return ParticipantTracks;
  }
  shouldOverrideAspectRatio() {
    return false;
  }
  shouldReplaceParticipantTabWithStrip() {
    return false;
  }
}
